import router from '@/router/router'
import Pagination from '@/types/Pagination.interface'
import Person, { Phone } from '@/types/Person.interface'
import User from '@/types/User.interface'

interface State {
    persons: Person[]
    personsRecent: Person[]
    preseededPersons: Person[]
}

const defaults = () => {
    return {
        persons: [],
        personsRecent: [],
        preseededPersons: null
    }
}

export default {
    state: defaults(),
    mutations: {
        personsPush (state: State, persons: Person[]): void {
            state.persons = window.removeDuplicatesById(state.persons, persons)
        },
        personsRecentPush (state: State, persons: Person[]): void {
            state.personsRecent = window.removeDuplicatesById(state.personsRecent, persons)
        },
        personsRemove (state: State, ids: number[]): void {
            //  Get our current list of persons
            const persons = state.persons

            //  If no persons, do nothing
            if (!persons) return

            //  Else, filter our our removed ids
            const filtered = persons.filter(x => !ids.includes(x.id))

            //  Set our persons to the filtered list
            state.persons = filtered
        },
        personsReset (state: State): void {
            Object.assign(state, defaults())
        },
        preseededPersonsSet (state: State, persons: Person[]): void {
            state.preseededPersons = persons
        }
    },
    actions: {
        personsCount ({ getters }): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/persons/count`

            //  send the request
            return window.axios.get(url).then(response => {
                //  REturn our total count
                return response.data
            }).catch(() => null)
        },
        // This action will modify the 'persons', 'contactsPersons', 'personsAutocompleteList', and 'listsSelectedGroupPersons' states
        personsDelete ({ commit, getters }, personId: number): Promise<Person> {
            //  Assemble the API endpoint
            const url = `${getters.api}/persons/${personId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove this person from our global dataset
                commit('personsRemove', [response.data.id])

                //  remove this person from our contacts
                commit('contactsPersonsRemove', [response.data.id])

                //  Update contacts searched persons
                commit('contactsSearchedPersonsRemove', [response.data.id])

                //  Remove the persons from our autocomplete
                commit('personsAutocompleteRemove', [response.data.id])

                //  Remove this person from our lists
                commit('listsSelectedGroupPersonsRemove', [response.data.id])

                //  Update inbox selected person
                commit('inboxSelectedPersonRemove', [response.data.id])
        
                //  Update users view selected person
                commit('teammatesSelectedPersonRemove', [response.data.id])

                //  Return the deleted person
                return response.data
            }).catch(() => null)
        },
        personsDeleteBatch ({ commit, getters }, ids: number[]): Promise<number[]> {
            //  Create our payload
            const payload = { data: { ids: ids}}

            //  Assemble the API endpoint
            const url = `${getters.api}/persons/batch`

            //  Send the request
            return window.axios.delete(url, payload).then(response => {
                //  Remove the persons from our global dataset
                commit('personsRemove', response.data)

                //  remove the persons from our contacts
                commit('contactsPersonsRemove', response.data)

                //  Update contacts searched persons
                commit('contactsSearchedPersonsRemove', response.data)

                //  Remove the persons from our autocomplete
                commit('personsAutocompleteRemove', response.data)

                //  Remove the persons from our lists
                commit('listsSelectedGroupPersonsRemove', response.data)

                //  Update inbox selected person
                commit('inboxSelectedPersonRemove', response.data)
        
                //  Update users view selected person
                commit('teammatesSelectedPersonRemove', response.data)

                //  Return our deleted ids
                return response.data
            }).catch(() => null)
        },
        personsFind ({ commit, getters }, personId: number): Promise<Person> {
            //  get the list of locally stored persons
            const persons = getters.persons

            //  Check if we have a local copy of the person
            const person = persons && persons.find(x => x.id === personId)

            //  If the person exists, return it
            if (person) return person

            //  Else, assemble the API endpoint
            const url = `${getters.api}/persons/${personId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Add the person to the global list
                commit('personsPush', [response.data])

                //  Return our found person
                return response.data
            }).catch(() => null)
        },
        personsGetQueryCount({ getters }, query): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/persons/query/count`

            //  Send the request
            return window.axios.post(url, query).then(response => {
                //  Return our count
                return response.data
            }).catch(() => null)
        },
        personsPaginate ({ commit, getters }): Promise<Pagination> {
            //  Create our initial pagination params
            const p = { page: 1 }

            //  Assemble the API endpoint
            const url = `${getters.api}/persons?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Handle contacts module related
                //  Update pagination params (this must occur BEFORE updating the persons list, for reset to work properly)
                commit('contactsPaginationPush', response.data)

                //  Update the contacts channels list
                commit('contactsPersonsPush', response.data.data)

                //  Handle persons autocomplete module related
                //  Update pagination params (this must occur BEFORE updating the persons list, for reset to work properly)
                commit('personsAutocompletePaginationPush', response.data)

                //  Update our persons autocomplete list
                commit('personsAutocompletePush', response.data.data)

                return response.data
            }).catch(() => null)
        },
        personsRecentPaginate ({ commit, getters }): Promise<Pagination> {
            //  Create our initial pagination params
            const p = { limit: 10, order_by: 'created_at DESC', page: 1}

            //  Assemble the API endpoint
            const url = `${getters.api}/persons?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set this in our store
                commit('personsRecentPush', response.data.data)

                return response.data.data
            }).catch(() => null)
        },
        // This action will modify the 'persons', 'contactsPersons', 'personsAutocompleteList' states
        personsPost ({ commit, getters }, person: Person): Promise<Person> {
            //  Assemble the api endpoint
            const url = `${getters.api}/persons`

            //  Send the request
            return window.axios.post(url, person).then(response => {
                //  Push to the global dataset
                commit('personsPush', [response.data])

                //  Update the contacts channels list
                commit('contactsPersonsPush', [response.data])

                //  Update our persons autocomplete list
                commit('personsAutocompletePush', [response.data])

                return response.data
            }).catch((e) => {
                //  If phone number is duplicate, reroute to merging
                if (e.response.data.errors['phone_default.free_form_number']) {
                    //  Get our tenant and data provider
                    const provider = getters.tenant.data_provider

                    //  If provider is NOT QBO and NOT XERO, route to merging
                    if (provider !== 'XERO' && provider !== 'QBO') router.push('/settings/merge-contacts')
                }
                return null
            })
        },
        personsPostBatch ({ getters }, persons: Person[]): Promise<string[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/persons/batch`

            //  Send the request
            return window.axios.post(url, persons).then(response => {
                //  Return the response informing about the job
                return response.data
            }).catch(() => null)
        },
        personsPreseeded ({ commit, getters }): Promise<Person[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/persons/preseed`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pre-seeded contacts
                commit('preseededPersonsSet', response.data)

                //  Return our persons
                return response.data
            })
        },
        // This action will modify the 'persons', 'contactsPersons', 'personsAutocompleteList', and 'listsSelectedGroupPersons' states
        personsPut ({ commit, getters }, person: Person): Promise<Person> {
            //  Assemble the api endpoint
            const url = `${getters.api}/persons`

            //  Send the request
            return window.axios.put(url, person).then(response => {
                //  Push to the global dataset
                commit('personsPush', [response.data])

                //  Update the contacts persons list
                commit('contactsPersonsSync', [response.data])

                //  Update the contacts searched list
                commit('contactsSearchedPersonsSync', [response.data])

                //  Update our persons autocomplete list
                commit('personsAutocompletePush', [response.data])

                //  Update our selected group persons if needed
                commit('listsSelectedGroupPersonsSync', [response.data])

                //  Update inbox channels
                commit('inboxChannelsPersonsUpdate', [response.data])

                //  Update the inbox persons list
                commit('inboxSelectedPersonSync', [response.data])

                //  Update teammates persons
                commit('teammatesSelectedPersonSync', [response.data])

                //  Return the updated person
                return response.data
            }).catch(() => null)
        },
        personsWhereIn ({ getters }, ids: number[]): Promise<Person[]> {
            //  Create our payload
            const payload = { person_ids: ids }

            //  Assemble the API endpoint
            const url = `${getters.api}/persons/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return our found persons
                return response.data
            }).catch(() => null)
        },
        personsWhereInChannels ({ commit, getters }, ids: number[]): Promise<Person[]> {
            //  Create our payload
            const payload = { channel_ids: ids }

            //  Assemble the API endpoint
            const url = `${getters.api}/persons/whereinchannels`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Add the persons to the global list
                commit('personsPush', response.data)
                
                //  Return response data (Array of Persons)
                return response.data
            }).catch(() => null)
        },
        personsWhereInUsers ({ commit, getters }, ids: number[]): Promise<Person[]> {
            //  Create our payload
            const payload = { user_ids: ids }

            //  Assemble the API endpoint
            const url = `${getters.api}/persons/whereinusers`

            //  Send the request
            return window.axios.post(url, payload).then(response => {

                //  Add the persons to the global list
                commit('personsPush', response.data)
            }).catch(() => null)
        },
        personsUser({ commit, getters }, user: User): Promise<Person> {
            //  get the list of locally stored persons
            const persons = getters.persons

            //  Check if we have a local copy of the person
            const person = persons && persons.find(x => x.user_id === user.id)

            //  If the person exists, return it
            if (person) return person

            //  Else, assemble the API endpoint
            const url = `${getters.api}/persons/whereinusers`

            //  Create our payload
            const payload = { user_ids: [user.id]}

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Add the person to the global list
                commit('personsPush', [response.data[0]])  //  We know there is a single record because we passed a single user id to the request

                //  Return our found person
                return response.data[0]
            }).catch(() => null)
        }
    },
    getters: {
        persons (state: State): Person[] {
            return state.persons
        },
        personsRecent (state: State): Person[] {
            return state.personsRecent
        },
        isPersonBlocked: () => (person: Person): boolean => {
            return person?.phone_default?.blocked !== null ? true : false
        },
        preseededPersons (state: State): Person[] {
            return state.preseededPersons
        }
    }
}